import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

export default function Systems() {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [comingSoonToast, setComingSoonToast] = useState(false);

  const systems = useMemo(
    () => [
      {
        name: t("systems.item1.name"),
        en: t("systems.item1.en"),
        description: t("systems.item1.desc"),
        icon: "ri-calendar-todo-line",
        path: "/experience",
      },
      {
        name: t("systems.item2.name"),
        en: t("systems.item2.en"),
        description: t("systems.item2.desc"),
        icon: "ri-star-smile-line",
      },
      {
        name: t("systems.item3.name"),
        en: t("systems.item3.en"),
        description: t("systems.item3.desc"),
        icon: "ri-hexagon-line",
      },
      {
        name: t("systems.item4.name"),
        en: t("systems.item4.en"),
        description: t("systems.item4.desc"),
        icon: "ri-bear-smile-line",
      },
      {
        name: t("systems.item5.name"),
        en: t("systems.item5.en"),
        description: t("systems.item5.desc"),
        icon: "ri-planet-line",
      },
      {
        name: t("systems.item6.name"),
        en: t("systems.item6.en"),
        description: t("systems.item6.desc"),
        icon: "ri-user-search-line",
      },
      {
        name: t("systems.item7.name"),
        en: t("systems.item7.en"),
        description: t("systems.item7.desc"),
        icon: "ri-time-line",
      },
      {
        name: t("systems.item8.name"),
        en: t("systems.item8.en"),
        description: t("systems.item8.desc"),
        icon: "ri-shining-line",
      },
      {
        name: t("systems.item9.name"),
        en: t("systems.item9.en"),
        description: t("systems.item9.desc"),
        icon: "ri-magic-line",
      },
      {
        name: t("systems.item10.name"),
        en: t("systems.item10.en"),
        description: t("systems.item10.desc"),
        icon: "ri-moon-line",
      },
    ],
    [t]
  );

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: "smooth" });
    setTimeout(checkScroll, 400);
  };

  const handleCardClick = (system: (typeof systems)[number]) => {
    if (system.path) {
      navigate(system.path);
    } else {
      setComingSoonToast(true);
      setTimeout(() => setComingSoonToast(false), 2500);
    }
  };

  return (
    <section
      id="systems"
      className={`relative py-24 sm:py-32 ${
        isLight ? "bg-[#f0f0f5]" : "bg-cosmic-950"
      }`}
    >
      {/* Coming Soon Toast */}
      <AnimatePresence>
        {comingSoonToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 whitespace-nowrap ${
              isLight
                ? "bg-[#1c1c1e] text-white"
                : "bg-white text-[#1c1c1e]"
            }`}
          >
            <i className="ri-time-line text-lg" />
            <span className="text-sm font-medium">即將上線，敬請期待</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Divider */}
      <div
        className={`mx-auto w-2/3 max-w-3xl h-px mb-24 sm:mb-32 bg-gradient-to-r from-transparent to-transparent ${
          isLight ? "via-black/10" : "via-white/10"
        }`}
      />

      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20 max-w-4xl mx-auto flex flex-col items-center gap-8 text-center"
        >
          <div>
            <h3
              className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-relaxed ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              {t("systems.title")}
            </h3>
            <p
              className={`mt-5 text-base md:text-lg ${
                isLight ? "text-[#6e6e73]" : "text-silver-400"
              }`}
            >
              {t("systems.subtitle")}
            </p>
          </div>
          <div className="flex items-end gap-10 sm:gap-14">
            <div className="text-center">
              <div
                className={`text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight ${
                  isLight ? "text-[#2d2d2d]/80" : "text-gold-500/80"
                }`}
              >
                9
              </div>
              <div
                className={`mt-2 text-base sm:text-lg ${
                  isLight ? "text-[#6e6e73]" : "text-silver-500"
                }`}
              >
                {t("systems.stat_systems")}
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight ${
                  isLight ? "text-[#2d2d2d]/80" : "text-gold-500/80"
                }`}
              >
                250+
              </div>
              <div
                className={`mt-2 text-base sm:text-lg ${
                  isLight ? "text-[#6e6e73]" : "text-silver-500"
                }`}
              >
                {t("systems.stat_tools")}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Horizontal Scroll Cards */}
      <div className="relative group">
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scrollBy(-1)}
            className={`absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors cursor-pointer ${
              isLight
                ? "bg-white/80 border border-black/10 text-[#1c1c1e] hover:bg-white"
                : "bg-cosmic-800/80 border border-white/10 text-white hover:bg-cosmic-700"
            }`}
            aria-label={t("systems.scroll_left")}
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
        )}

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scrollBy(1)}
            className={`absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors cursor-pointer ${
              isLight
                ? "bg-white/80 border border-black/10 text-[#1c1c1e] hover:bg-white"
                : "bg-cosmic-800/80 border border-white/10 text-white hover:bg-cosmic-700"
            }`}
            aria-label={t("systems.scroll_right")}
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 sm:px-10 pb-4 scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {/* Spacer for first card snap alignment on desktop */}
          <div className="hidden lg:block shrink-0 w-[calc((100vw-1280px)/2)]" />

          {systems.map((system, index) => (
            <motion.div
              key={system.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              onClick={() => handleCardClick(system)}
              className={`snap-start shrink-0 w-[300px] sm:w-[340px] rounded-2xl border backdrop-blur-sm p-7 sm:p-8 flex flex-col transition-all duration-300 cursor-pointer ${
                isLight
                  ? "border-black/10 bg-white/70 hover:border-[#2d2d2d]/30 hover:bg-white"
                  : "border-white/10 bg-cosmic-900/60 hover:border-gold-500/30 hover:bg-cosmic-800/60"
              }`}
            >
              <div
                className={`mb-5 w-12 h-12 flex items-center justify-center rounded-xl border ${
                  isLight
                    ? "bg-[#2d2d2d]/10 border-[#2d2d2d]/20"
                    : "bg-gold-500/10 border-gold-500/20"
                }`}
              >
                <i
                  className={`${system.icon} text-xl ${
                    isLight ? "text-[#2d2d2d]" : "text-gold-500"
                  }`}
                />
              </div>
              <h4
                className={`text-xl font-semibold mb-1 ${
                  isLight ? "text-[#1c1c1e]" : "text-white"
                }`}
              >
                {system.name}
              </h4>
              <span
                className={`text-sm italic mb-4 ${
                  isLight ? "text-[#6e6e73]/60" : "text-silver-500/60"
                }`}
              >
                {system.en}
              </span>
              <p
                className={`text-sm leading-7 flex-1 ${
                  isLight ? "text-[#6e6e73]/90" : "text-silver-400/90"
                }`}
              >
                {system.description}
              </p>
              <div
                className={`mt-5 pt-5 border-t flex items-center gap-2 text-xs ${
                  isLight
                    ? "border-black/5 text-[#2d2d2d]/70"
                    : "border-white/5 text-gold-500/70"
                }`}
              >
                <i className="ri-arrow-right-line" />
                <span className="font-medium">馬上體驗</span>
              </div>
            </motion.div>
          ))}

          {/* Spacer for last card snap alignment on desktop */}
          <div className="hidden lg:block shrink-0 w-[calc((100vw-1280px)/2)]" />
        </div>
      </div>

      {/* Scroll hint */}
      <div
        className={`mt-6 flex items-center justify-center gap-2 text-xs ${
          isLight ? "text-[#6e6e73]/50" : "text-silver-500/50"
        }`}
      >
        <i className="ri-drag-move-line" />
        <span>{t("systems.scroll_hint")}</span>
      </div>
    </section>
  );
}